// src/main.js
// Ponto de entrada do app
// Controla estado global, autenticação e conecta todos os módulos

import { onAuthChange, loginWithGoogle, logout, getUserProfile, ensureProfile } from './auth.js'
import { listenToGroups, createGroup, listDMs, updateGroup, deleteGroup, listenToInstance, getOrCreateInstance, getTodayStatuses, markStatusViewed, createStatus, getUserByEmail, updateUserRole, updateLastRead, updateDMLastRead, deleteDM } from './db.js'
import { serverTimestamp } from 'firebase/firestore'
import { generateTeacherMessage } from './ai.js'
import {
  openGroupInstance, sendGroupMessage,
  openDM, startDMGreeting, sendDMMessage,
} from './chat.js'
import {
  renderLoginScreen, hideLoginScreen, renderChatList, renderChatHeader,
  renderMessages, appendMessage, setTyping,
  openPanel, clearPanels,
  buildPhilGrid, getSelectedPhils, showToast, ICONS,
  renderStatusList, showStatusViewer, closeStatusViewer, updateStatusBadge,
  showAdminPanel, closeAdminPanel, renderAdminUserResult,
  renderContactsList,
} from './ui.js'
import { PHILOSOPHERS, TEACHER } from './data/philosophers.js'
import { auth } from './firebase.js'
import { onAuthStateChanged } from 'firebase/auth'

// ── ESTADO GLOBAL ───────────────────────────────────────────────────────────
const state = {
  user:           null,
  userProfile:    null,
  groups:         [],        // templates do Firestore
  chats:          [],        // lista unificada (grupos + DMs) exibida na sidebar
  activeChat:     null,      // chat atualmente aberto
  filter:         'all',
  isLoading:      false,
  unsubActive:    null,      // unsubscribe do listener do chat ativo
  unsubListeners: {},        // unsubscribes dos listeners de notificação por chatId
}

// ── BOOT ─────────────────────────────────────────────────────────────────────
// Usa signInWithPopup (não redirect) — mais confiável nos browsers modernos.
// O onAuthStateChanged cuida de tudo: login, logout e sessão persistida.

let appInitialized = false

onAuthStateChanged(auth, async (user) => {
  if (!user) {
    if (appInitialized) {
      appInitialized = false
      state.unsubActive?.()
      state.unsubActive = null
      // Cancela todos os listeners de notificação
      Object.values(state.unsubListeners).forEach((unsub) => unsub())
      state.unsubListeners = {}
      state.user        = null
      state.userProfile = null
      state.chats       = []
      state.activeChat  = null
    }
    renderLoginScreen()
    document.getElementById('login-btn')
      ?.addEventListener('click', async () => {
        try {
          await loginWithGoogle()
        } catch (e) {
          showToast('Erro ao fazer login. Tente novamente.', '#c0443a')
          // Re-add listener since { once: true } consumed it
          document.getElementById('login-btn')
            ?.addEventListener('click', loginWithGoogle, { once: true })
        }
      }, { once: true })
    return
  }

  // Usuário autenticado
  hideLoginScreen()
  state.user = user

  // Atualiza avatar do nav rail
  const navAv = document.getElementById('nav-profile-av')
  if (navAv && user.photoURL) {
    navAv.innerHTML = `<img src="${user.photoURL}" alt="" referrerpolicy="no-referrer">`
  }

  try {
    await ensureProfile(user)
    state.userProfile = await getUserProfile(user.uid)
  } catch (e) {
    console.warn('Perfil indisponível (offline?) — continuando sem perfil:', e.message)
  }

  if (!appInitialized) {
    appInitialized = true
    initApp()
  }
})

// ── INICIALIZA APP ───────────────────────────────────────────────────────────
function initApp() {
  // Mostra/esconde botões exclusivos de professor
  if (state.userProfile?.role === 'teacher') {
    const adminBtn = document.getElementById('btn-admin')
    if (adminBtn) adminBtn.style.display = ''
    const hdAdmin = document.getElementById('hd-admin')
    if (hdAdmin) hdAdmin.style.display = ''
  } else {
    const newGroupBtn = document.getElementById('btn-new-group')
    if (newGroupBtn) newGroupBtn.style.display = 'none'
  }

  // Carrega cache para renderizar sidebar imediatamente
  _loadChatsCache()
  if (state.chats.length > 0) rerenderSidebar()

  // Escuta grupos em tempo real (atualiza em background)
  listenToGroups((groups) => {
    state.groups = groups
    syncChatsFromGroups(groups)
    _saveChatsCache()
    rerenderSidebar()
  })

  // Carrega DMs persistidos do Firestore
  loadUserDMs(state.user.uid)

  // Carrega badge de statuses não vistos
  refreshStatusBadge()

  // Botão voltar do celular
  window.addEventListener('popstate', () => {
    if (document.body.classList.contains('chat-open')) {
      document.body.classList.remove('chat-open')
      if (window.innerWidth > 768) {
        document.getElementById('chat-view').style.display = 'none'
        document.getElementById('empty-state').style.display = ''
      }
      state.activeChat = null
      clearPanels()
      rerenderSidebar()
    } else if (document.getElementById('page-contacts')?.style.display !== 'none') {
      switchToPage('chats')
    } else if (document.getElementById('page-status')?.style.display !== 'none') {
      switchToPage('chats')
    }
  })

  // Event listeners do DOM
  setupEventListeners()
}

// Carrega DMs do Firestore e mescla na lista de chats
async function loadUserDMs(userId) {
  try {
    const dms = await listDMs(userId)
    dms.forEach((dm) => {
      const phil = PHILOSOPHERS[dm.philId]
      if (!phil) return
      const existing = state.chats.find((c) => c.type === 'dm' && c.philId === dm.philId)
      if (!existing) {
        state.chats.push({
          id:       dm.id,
          type:     'dm',
          name:     phil.name,
          philId:   dm.philId,
          messages: dm.messages,
          unread:   Math.max(0, dm.messages.length - (dm.lastReadCount ?? 0)),
        })
      }
    })
    _cachedDMs = [] // DMs do Firestore carregados — cache não é mais necessário
    _saveChatsCache()
    rerenderSidebar()
  } catch (e) {
    console.error('Erro ao carregar DMs:', e)
  }
}

// Constrói a lista de chats a partir dos grupos do Firestore + DMs em memória
function syncChatsFromGroups(groups) {
  // Preserva TODOS os DMs existentes (carregados do Firestore OU abertos na sessão)
  const existingDMs = state.chats.filter((c) => c.type === 'dm')
  // Também preserva DMs do cache que ainda não foram sobrescritos
  const cachedDMs = _cachedDMs.filter((dm) =>
    !existingDMs.find((e) => e.philId === dm.philId)
  )

  const groupChats = groups.map((g) => {
    const existing = state.chats.find((c) => c.type === 'group' && c.id === g.id)
    return {
      ...g,
      type:     'group',
      messages: existing?.messages || [],
      unread:   existing?.unread   || 0,
    }
  })

  state.chats = [...groupChats, ...existingDMs, ...cachedDMs]

  // Registra listeners de notificação para cada grupo
  if (state.user) {
    groups.forEach((g) => {
      if (state.unsubListeners[g.id]) return // já tem listener
      state.unsubListeners[g.id] = listenToInstance(g.id, state.user.uid, (data) => {
        const messages = data.messages || []
        const lastReadCount = data.lastReadCount ?? 0
        updateChatMessages(g.id, messages, lastReadCount)
      })
    })
  }
}

// ── SIDEBAR ──────────────────────────────────────────────────────────────────
function rerenderSidebar() {
  renderChatList({
    chats:    state.chats,
    activeId: state.activeChat?.id,
    filter:   state.filter,
    query:    document.getElementById('search-input')?.value || '',
    onOpen:   openChat,
  })
}

// ── NAV RAIL — alterna entre Conversas e Status ─────────────────────────────
function switchToPage(page) {
  const pages = ['page-chats', 'page-status', 'page-contacts']
  pages.forEach((id) => {
    const el = document.getElementById(id)
    if (el) el.style.display = 'none'
  })

  const navChats  = document.getElementById('nav-chats')
  const navStatus = document.getElementById('nav-status')
  navChats?.classList.remove('active')
  navStatus?.classList.remove('active')

  if (page === 'status') {
    document.getElementById('page-status').style.display = 'flex'
    navStatus?.classList.add('active')
    renderStatusView()
  } else if (page === 'contacts') {
    document.getElementById('page-contacts').style.display = 'flex'
    history.pushState({ view: 'contacts' }, '')
    renderContactsList(
      (philId) => { switchToPage('chats'); openOrCreateDM(philId) },
      (philId) => openPanel('phil', { philId, onDM: openOrCreateDM }),
    )
  } else {
    document.getElementById('page-chats').style.display = 'flex'
    navChats?.classList.add('active')
    rerenderSidebar()
  }
}

// ── STATUS VIEW ─────────────────────────────────────────────────────────────
async function renderStatusView() {
  try {
    const statuses = await getTodayStatuses()
    const viewedIds = statuses
      .filter((s) => (s.views || []).includes(state.user?.uid))
      .map((s) => s.id)

    renderStatusList(statuses, viewedIds, (status) => {
      showStatusViewer(status, state.user.uid, async () => {
        await markStatusViewed(status.id, state.user.uid)
        refreshStatusBadge()
      })
    })
  } catch (e) {
    console.warn('Erro ao carregar statuses:', e.message)
    renderStatusList([], [], () => {})
  }
}

// ── CACHE DE CHATS ──────────────────────────────────────────────────────────
const CACHE_KEY = 'philosapp_chats_cache'
let _cachedDMs = [] // DMs do cache preservados durante a race condition do listenToGroups

function _saveChatsCache() {
  try {
    const data = state.chats.map((c) => {
      const last = (c.messages || []).slice(-1)[0]
      return {
        id: c.id, type: c.type, name: c.name, philId: c.philId,
        philIds: c.philIds, classTime: c.classTime, topic: c.topic,
        // Só metadados da última msg para preview — nunca conteúdo completo
        messages: last ? [{ sender: last.sender, text: last.text.substring(0, 60), ts: last.ts }] : [],
        unread: c.unread || 0,
      }
    })
    localStorage.setItem(CACHE_KEY, JSON.stringify(data))
  } catch { /* quota exceeded etc */ }
}

function _loadChatsCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return
    const data = JSON.parse(raw)
    if (Array.isArray(data) && data.length > 0) {
      state.chats = data
      _cachedDMs = data.filter((c) => c.type === 'dm')
    }
  } catch { /* parse error */ }
}

async function refreshStatusBadge() {
  try {
    const statuses = await getTodayStatuses()
    const unseen = statuses.filter((s) => !(s.views || []).includes(state.user?.uid)).length
    updateStatusBadge(unseen)
  } catch { /* offline */ }
}

// ── DEV: seed test statuses ─────────────────────────────────────────────────
async function seedTestStatuses() {
  const philIds = Object.keys(PHILOSOPHERS)
  const shuffled = [...philIds].sort(() => Math.random() - 0.5)
  const selected = shuffled.slice(0, 3 + Math.floor(Math.random() * 3))

  const captions = [
    'O que não te desafia, não te transforma.',
    'Questione tudo — inclusive esta frase.',
    'A vida sem reflexão não vale a pena ser vivida.',
    'O caminho se faz caminhando.',
    'Não existe vento favorável para quem não sabe onde quer chegar.',
  ]

  for (let i = 0; i < selected.length; i++) {
    const phil = PHILOSOPHERS[selected[i]]
    await createStatus({
      philId: selected[i],
      imageURL: `https://source.unsplash.com/random/600x900?${encodeURIComponent(phil.unsplashQuery || 'philosophy')}`,
      caption: captions[i % captions.length],
    })
  }
  console.log(`Criados ${selected.length} statuses de teste:`, selected.join(', '))
  refreshStatusBadge()
  if (state.filter === 'status') renderStatusView()
}

// Expõe no console para dev: window.seedTestStatuses()
window.seedTestStatuses = seedTestStatuses

// ── ABRIR CHAT ───────────────────────────────────────────────────────────────
async function openChat(chat) {
  // Fecha listener anterior
  state.unsubActive?.()
  state.unsubActive = null

  state.activeChat = chat
  chat.unread = 0

  // Mostra o painel de chat
  document.getElementById('empty-state').style.display  = 'none'
  document.getElementById('chat-view').style.display    = 'flex'
  document.getElementById('chat-view').style.flexDirection = 'flex'
  document.body.classList.add('chat-open')
  history.pushState({ view: 'chat' }, '')
  clearPanels()

  renderChatHeader(chat)

  if (chat.type === 'group') {
    await openGroupChat(chat)
  } else {
    await openDMChat(chat)
  }

  // Persiste estado de leitura
  _markChatRead(chat)

  rerenderSidebar()
}

async function _markChatRead(chat) {
  const count = chat.messages?.length || 0
  try {
    if (chat.type === 'group') {
      await updateLastRead(chat.id, state.user.uid, count)
    } else if (chat.type === 'dm' && chat.philId) {
      await updateDMLastRead(state.user.uid, chat.philId, count)
    }
  } catch { /* offline — ignora */ }
}

async function openGroupChat(chat) {
  const group = state.groups.find((g) => g.id === chat.id)
  if (!group) return

  const { messages, unsubscribe } = await openGroupInstance({
    groupId:   group.id,
    userId:    state.user.uid,
    group,
    onMessage: (msgs) => {
      updateChatMessages(group.id, msgs)
      if (state.activeChat?.id === group.id) renderMessages(msgs, 'group')
    },
    onTyping: (name, emoji, color, visible) => {
      if (state.activeChat?.id === group.id) setTyping(name, emoji, color, visible)
    },
  })

  state.unsubActive = unsubscribe
  updateChatMessages(group.id, messages)
  renderMessages(messages, 'group')
}

async function openDMChat(chat) {
  const { messages, unsubscribe } = await openDM({
    userId:    state.user.uid,
    philId:    chat.philId,
    onMessage: (messages) => {
      updateChatMessages(chat.id, messages)
      if (state.activeChat?.id === chat.id) renderMessages(messages, 'dm')
    },
  })

  state.unsubActive = unsubscribe
  updateChatMessages(chat.id, messages)
  renderMessages(messages, 'dm')

  // Se DM vazio, gera saudação inicial
  if (messages.length === 0) {
    await startDMGreeting({
      userId:   state.user.uid,
      philId:   chat.philId,
      onTyping: (name, emoji, color, visible) => {
        if (state.activeChat?.id === chat.id) setTyping(name, emoji, color, visible)
      },
    })
  }
}

function updateChatMessages(chatId, messages, lastReadCount) {
  const chat = state.chats.find((c) => c.id === chatId)
  if (chat) {
    chat.messages = messages
    if (state.activeChat?.id === chatId) {
      // Chat ativo — tudo lido, persiste
      chat.unread = 0
      _markChatRead(chat)
    } else if (lastReadCount !== undefined) {
      // Usa lastReadCount do Firestore
      chat.unread = Math.max(0, messages.length - lastReadCount)
    }
    rerenderSidebar()
  }
}

// ── ENVIAR MENSAGEM ───────────────────────────────────────────────────────────
async function sendMessage() {
  if (state.isLoading || !state.activeChat) return
  const input = document.getElementById('msg-input')
  const text  = input.value.trim()
  if (!text) return

  input.value = ''
  input.style.height = 'auto'
  state.isLoading = true
  document.getElementById('send-btn').disabled = true

  const chat = state.activeChat

  try {
    if (chat.type === 'group') {
      const group = state.groups.find((g) => g.id === chat.id)
      if (!group) return
      await sendGroupMessage({
        groupId:         group.id,
        userId:          state.user.uid,
        group,
        text,
        currentMessages: chat.messages || [],
        onTyping: (name, emoji, color, visible) => {
          if (state.activeChat?.id === chat.id) setTyping(name, emoji, color, visible)
        },
        onError: (e) => showToast(`Erro na resposta: ${e.message || 'tente novamente'}`, '#c0443a'),
      })
    } else {
      await sendDMMessage({
        userId:          state.user.uid,
        philId:          chat.philId,
        text,
        currentMessages: chat.messages || [],
        onTyping: (name, emoji, color, visible) => {
          if (state.activeChat?.id === chat.id) setTyping(name, emoji, color, visible)
        },
        onError: (e) => showToast(`Erro na resposta: ${e.message || 'tente novamente'}`, '#c0443a'),
      })
    }
  } catch (e) {
    console.error('Erro ao enviar mensagem:', e)
    showToast('Erro ao enviar mensagem. Tente novamente.', '#c0443a')
  }

  state.isLoading = false
  document.getElementById('send-btn').disabled = false
}

// ── DM ────────────────────────────────────────────────────────────────────────
function openOrCreateDM(philId) {
  const p = PHILOSOPHERS[philId]
  if (!p) return

  let dm = state.chats.find((c) => c.type === 'dm' && c.philId === philId)
  if (!dm) {
    dm = {
      id:       `dm_${philId}`,
      type:     'dm',
      name:     p.name,
      philId,
      messages: [],
      unread:   0,
    }
    state.chats.push(dm)
  }

  // Muda filtro para "Mensagens"
  setFilter('dms', document.querySelector('[data-filter="dms"]'))
  openChat(dm)
}

// ── CRIAR GRUPO ───────────────────────────────────────────────────────────────
async function handleCreateGroup() {
  const name      = document.getElementById('g-name').value.trim()
  const topic     = document.getElementById('g-topic').value.trim()
  const classTime = document.getElementById('g-time')?.value || ''
  const philIds   = getSelectedPhils()

  if (!name)            return showToast('Informe o nome do grupo')
  if (!topic)           return showToast('Informe o tema da aula')
  if (philIds.length < 2) return showToast('Selecione ao menos 2 filósofos')

  const btn = document.getElementById('btn-create-group')
  const originalText = btn.textContent
  btn.disabled = true
  btn.textContent = 'Criando grupo…'

  try {
    const groupId = await createGroup({
      name, topic, classTime, philIds,
      createdBy: state.user.uid,
    })

    // Gera mensagem da professora imediatamente
    btn.textContent = 'Gerando aula…'
    const teacherText = await generateTeacherMessage(
      TEACHER.system(topic, name),
      name,
      topic,
    )

    await updateGroup(groupId, {
      teacherMessage:   teacherText,
      teacherMessageTs: serverTimestamp(),
    })

    closeModal()
    showToast(`Grupo "${name}" criado — aula pronta!`)

    // Limpa formulário
    document.getElementById('g-name').value  = ''
    document.getElementById('g-topic').value = ''
    if (document.getElementById('g-time')) document.getElementById('g-time').value = ''
  } catch (e) {
    console.error(e)
    showToast('Erro ao criar grupo', '#c0443a')
  } finally {
    btn.disabled = false
    btn.textContent = originalText
  }
}

// ── FILTROS ──────────────────────────────────────────────────────────────────
function setFilter(f, el) {
  state.filter = f
  document.querySelectorAll('.filter-pill').forEach((p) => p.classList.remove('active'))
  el?.classList.add('active')
  rerenderSidebar()
}

// ── MODAL ─────────────────────────────────────────────────────────────────────
function openModal() {
  buildPhilGrid([], () => {})
  document.getElementById('modal-overlay').classList.add('open')
}
function closeModal() {
  document.getElementById('modal-overlay').classList.remove('open')
}

// ── EVENT LISTENERS ───────────────────────────────────────────────────────────
function setupEventListeners() {
  // Enviar mensagem
  document.getElementById('send-btn')?.addEventListener('click', sendMessage)
  document.getElementById('msg-input')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() }
  })
  document.getElementById('msg-input')?.addEventListener('input', (e) => {
    e.target.style.height = 'auto'
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'
  })

  // Busca
  document.getElementById('search-input')?.addEventListener('input', rerenderSidebar)

  // Contatos
  document.getElementById('btn-contacts')?.addEventListener('click', () => switchToPage('contacts'))
  document.getElementById('btn-contacts-back')?.addEventListener('click', () => switchToPage('chats'))
  document.getElementById('btn-status-back')?.addEventListener('click', () => switchToPage('chats'))

  // Voltar para lista (mobile)
  document.getElementById('btn-back-to-list')?.addEventListener('click', backToList)

  function backToList() {
    document.body.classList.remove('chat-open')
    // No desktop, reseta a área principal
    if (window.innerWidth > 768) {
      document.getElementById('chat-view').style.display = 'none'
      document.getElementById('empty-state').style.display = ''
    }
    state.activeChat = null
    clearPanels()
    rerenderSidebar()
  }

  // Filtros
  document.querySelectorAll('.filter-pill').forEach((pill) => {
    pill.addEventListener('click', () => setFilter(pill.dataset.filter, pill))
  })

  // Novo grupo
  document.getElementById('btn-new-group')?.addEventListener('click', openModal)
  document.getElementById('btn-cancel-modal')?.addEventListener('click', closeModal)
  document.getElementById('btn-create-group')?.addEventListener('click', handleCreateGroup)

  // Fechar modal clicando fora
  document.getElementById('modal-overlay')?.addEventListener('click', (e) => {
    if (e.target.id === 'modal-overlay') closeModal()
  })

  // Header do chat → abre painel lateral
  document.getElementById('chat-header')?.addEventListener('click', () => {
    if (!state.activeChat) return
    if (state.activeChat.type === 'dm') {
      openPanel('phil', { philId: state.activeChat.philId, onDM: openOrCreateDM })
    } else if (state.activeChat.type === 'group') {
      const group = state.groups.find((g) => g.id === state.activeChat.id)
      openPanel('group', {
        chat: { ...state.activeChat, topic: group?.topic, classTime: group?.classTime, createdAt: group?.createdAt },
        onDM: openOrCreateDM,
      })
    }
  })

  // ── Busca dentro do chat ──────────────────────────────────────
  const searchBar   = document.getElementById('chat-search-bar')
  const searchInput = document.getElementById('chat-search-input')
  const searchCount = document.getElementById('chat-search-count')
  let searchMatches = []
  let searchIndex   = -1

  document.getElementById('btn-chat-search')?.addEventListener('click', () => {
    const isOpen = searchBar.style.display !== 'none'
    if (isOpen) {
      closeChatSearch()
    } else {
      searchBar.style.display = 'flex'
      searchInput.focus()
    }
  })

  document.getElementById('chat-search-close')?.addEventListener('click', closeChatSearch)

  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.trim().toLowerCase()
    clearSearchHighlights()
    searchMatches = []
    searchIndex = -1

    if (!query) { searchCount.textContent = ''; return }

    // Encontra todos os textos de bolha que contêm a query
    const bubbles = document.querySelectorAll('#messages-wrap .bubble')
    bubbles.forEach((bubble) => {
      // Pega o div de texto (pula o sender e time)
      const textDivs = [...bubble.children].filter(
        (el) => !el.classList.contains('bubble-sender') && !el.classList.contains('bubble-time')
      )
      textDivs.forEach((div) => {
        const original = div.textContent
        if (!original.toLowerCase().includes(query)) return

        // Highlight com regex
        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
        div.innerHTML = original.replace(regex, '<mark class="search-highlight">$1</mark>')

        div.querySelectorAll('.search-highlight').forEach((mark) => {
          searchMatches.push(mark)
        })
      })
    })

    if (searchMatches.length > 0) {
      searchIndex = 0
      highlightCurrent()
    }
    searchCount.textContent = searchMatches.length > 0
      ? `${searchIndex + 1}/${searchMatches.length}`
      : 'Nenhum resultado'
  })

  document.getElementById('chat-search-up')?.addEventListener('click', () => {
    if (searchMatches.length === 0) return
    searchIndex = (searchIndex - 1 + searchMatches.length) % searchMatches.length
    highlightCurrent()
  })

  document.getElementById('chat-search-down')?.addEventListener('click', () => {
    if (searchMatches.length === 0) return
    searchIndex = (searchIndex + 1) % searchMatches.length
    highlightCurrent()
  })

  searchInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (searchMatches.length === 0) return
      searchIndex = (searchIndex + (e.shiftKey ? -1 : 1) + searchMatches.length) % searchMatches.length
      highlightCurrent()
    }
    if (e.key === 'Escape') closeChatSearch()
  })

  function highlightCurrent() {
    searchMatches.forEach((m, i) => {
      m.classList.toggle('active', i === searchIndex)
    })
    searchMatches[searchIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    searchCount.textContent = `${searchIndex + 1}/${searchMatches.length}`
  }

  function clearSearchHighlights() {
    document.querySelectorAll('#messages-wrap .search-highlight').forEach((mark) => {
      const parent = mark.parentNode
      parent.replaceChild(document.createTextNode(mark.textContent), mark)
      parent.normalize()
    })
    searchMatches = []
    searchIndex = -1
  }

  function closeChatSearch() {
    searchBar.style.display = 'none'
    searchInput.value = ''
    searchCount.textContent = ''
    clearSearchHighlights()
  }

  // Clique em avatar de filósofo nas mensagens
  document.getElementById('messages-wrap')?.addEventListener('click', (e) => {
    const av = e.target.closest('.msg-av[data-phil-id]')
    if (av) openPanel('phil', { philId: av.dataset.philId, onDM: openOrCreateDM })
  })

  // Dropdown menu dos 3 pontinhos no header do chat
  const chatDropdown = document.getElementById('chat-dropdown')
  document.getElementById('btn-chat-menu')?.addEventListener('click', () => {
    const isOpen = chatDropdown.style.display !== 'none'
    chatDropdown.style.display = isOpen ? 'none' : 'block'

    // Mostra/esconde opções baseado no tipo de chat e role
    const deleteGroupBtn = document.getElementById('btn-delete-group')
    const deleteDMBtn = document.getElementById('btn-delete-dm')
    if (deleteGroupBtn) {
      deleteGroupBtn.style.display =
        state.activeChat?.type === 'group' && state.userProfile?.role === 'teacher'
          ? '' : 'none'
    }
    if (deleteDMBtn) {
      deleteDMBtn.style.display =
        state.activeChat?.type === 'dm' ? '' : 'none'
    }
  })

  // Fecha dropdown ao clicar fora
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.dropdown-wrap') && chatDropdown) {
      chatDropdown.style.display = 'none'
    }
  })

  // Excluir grupo
  document.getElementById('btn-delete-group')?.addEventListener('click', async () => {
    chatDropdown.style.display = 'none'
    if (!state.activeChat || state.activeChat.type !== 'group') return

    const name = state.activeChat.name
    if (!confirm(`Tem certeza que deseja excluir o grupo "${name}"? Esta ação não pode ser desfeita.`)) return

    try {
      await deleteGroup(state.activeChat.id)
      state.activeChat = null
      document.getElementById('chat-view').style.display = 'none'
      document.getElementById('empty-state').style.display = ''
      showToast(`Grupo "${name}" excluído`)
    } catch (e) {
      console.error('Erro ao excluir grupo:', e)
      showToast('Erro ao excluir grupo', '#c0443a')
    }
  })

  // Excluir DM
  document.getElementById('btn-delete-dm')?.addEventListener('click', async () => {
    chatDropdown.style.display = 'none'
    if (!state.activeChat || state.activeChat.type !== 'dm') return

    const name = state.activeChat.name
    if (!confirm(`Apagar conversa com ${name}?`)) return

    try {
      await deleteDM(state.user.uid, state.activeChat.philId)
      state.chats = state.chats.filter((c) => c.id !== state.activeChat.id)
      state.activeChat = null
      document.body.classList.remove('chat-open')
      document.getElementById('chat-view').style.display = 'none'
      document.getElementById('empty-state').style.display = ''
      _saveChatsCache()
      rerenderSidebar()
      showToast(`Conversa com ${name} apagada`)
    } catch (e) {
      console.error('Erro ao apagar conversa:', e)
      showToast('Erro ao apagar conversa', '#c0443a')
    }
  })

  // Nav rail — alterna entre páginas
  document.getElementById('nav-chats')?.addEventListener('click', () => switchToPage('chats'))
  document.getElementById('nav-status')?.addEventListener('click', () => switchToPage('status'))

  // Fechar painel de perfil legado (X estático do #profile-panel)
  document.getElementById('pp-close')?.addEventListener('click', clearPanels)

  // Admin panel
  document.getElementById('btn-admin')?.addEventListener('click', () => {
    showAdminPanel()
  })
  document.getElementById('admin-panel-close')?.addEventListener('click', closeAdminPanel)
  document.getElementById('admin-search-btn')?.addEventListener('click', adminSearchUser)

  async function adminSearchUser() {
    const email = document.getElementById('admin-search-email')?.value.trim()
    if (!email) return showToast('Digite um e-mail')
    try {
      const user = await getUserByEmail(email)
      if (!user) {
        document.getElementById('admin-user-result').style.display = 'none'
        return showToast('Usuário não encontrado. O usuário precisa ter feito login pelo menos uma vez.', '#c0443a')
      }
      renderAdminUserResult(user, async (uid, newRole) => {
        try {
          await updateUserRole(uid, newRole)
          showToast(`${user.name || user.email} agora é ${newRole === 'teacher' ? 'Professor' : 'Aluno'}`)
          // Re-busca e re-renderiza com role atualizado
          const refreshed = await getUserByEmail(email)
          if (refreshed) renderAdminUserResult(refreshed, async (uid2, role2) => {
            await updateUserRole(uid2, role2)
            showToast(`Alterado para ${role2 === 'teacher' ? 'Professor' : 'Aluno'}`)
            const r2 = await getUserByEmail(email)
            if (r2) renderAdminUserResult(r2, () => {})
          })
        } catch (e) {
          console.error('Erro ao alterar role:', e)
          showToast('Erro ao alterar role', '#c0443a')
        }
      })
    } catch (e) {
      console.error('Erro na busca:', e)
      showToast('Erro ao buscar usuário', '#c0443a')
    }
  }

  // ── Header dropdown (⋮ menu na sidebar) ────────────────────────
  const headerDropdown = document.getElementById('header-dropdown')
  document.getElementById('btn-header-menu')?.addEventListener('click', () => {
    headerDropdown.style.display = headerDropdown.style.display === 'none' ? 'block' : 'none'
  })
  document.addEventListener('click', (e) => {
    if (!e.target.closest('#btn-header-menu') && !e.target.closest('#header-dropdown')) {
      if (headerDropdown) headerDropdown.style.display = 'none'
    }
  })

  // Status button no header (mobile)
  document.getElementById('btn-header-status')?.addEventListener('click', () => switchToPage('status'))

  // Dropdown: Admin
  document.getElementById('hd-admin')?.addEventListener('click', () => {
    headerDropdown.style.display = 'none'
    showAdminPanel()
  })

  // Dropdown: Sair
  document.getElementById('hd-logout')?.addEventListener('click', async () => {
    headerDropdown.style.display = 'none'
    state.unsubActive?.()
    state.unsubActive = null
    Object.values(state.unsubListeners).forEach((unsub) => unsub())
    state.unsubListeners = {}
    await logout()
  })

  // Logout (nav rail — desktop)
  document.getElementById('btn-logout')?.addEventListener('click', async () => {
    state.unsubActive?.()
    state.unsubActive = null
    Object.values(state.unsubListeners).forEach((unsub) => unsub())
    state.unsubListeners = {}
    await logout()
  })
}